import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cn } from "../../lib/utils";

// ─── AccessState ───────────────────────────────────────────────────────────────
//
// One shared screen for every blocked / empty / no-access state in the app:
// subscription gates, "access restricted" screens, and certification empty
// states. Replaces ~12 hand-rolled, drifted layouts with a single component
// modelled on `not-found.tsx` (centered, muted body, real Button) and
// `AppAlertModal` (rounded-2xl tinted icon badge + variant map).
//
// Server-safe by design — no hooks, no "use client". Actions are passed as
// React nodes (e.g. `<Button asChild><Link/></Button>` or a mailto button), so
// it renders from both Server Components (the access-restricted pages) and
// Client Components (the subscription gate).
//
// Usage:
//   <AccessState
//     icon={<LockKeyhole />}
//     title="Subscription required"
//     description="Your organisation doesn't have an active subscription."
//     action={
//       <Button asChild>
//         <Link href="/subscription/plans">Manage subscription</Link>
//       </Button>
//     }
//   />

type IconVariant = "default" | "warning" | "destructive";

// `default` is intentionally neutral (not the primary/brand token) so a blocked
// state reads as "inactive" and stays grey in every app theme; warning/destructive
// mirror AppAlertModal's tinted variants for states that need emphasis.
const ICON_VARIANT_CLASSES: Record<IconVariant, string> = {
  default: "bg-muted text-muted-foreground",
  warning: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  destructive: "bg-destructive/10 text-destructive",
};

const accessStateVariants = cva("flex flex-col", {
  variants: {
    // `page` fills the viewport for full-page gates; `section` sits inside a
    // page's content area; `inline` is a compact empty state inside a tab/card.
    size: {
      page: "min-h-[60vh] justify-center gap-6",
      section: "gap-6 py-24",
      inline: "gap-4 py-10",
    },
    align: {
      center: "items-center text-center",
      start: "items-start text-left",
    },
  },
  defaultVariants: {
    size: "page",
    align: "center",
  },
});

const titleVariants = cva("font-semibold", {
  variants: {
    size: {
      page: "text-xl",
      section: "text-xl",
      inline: "text-base",
    },
  },
  defaultVariants: {
    size: "page",
  },
});

export interface AccessStateProps
  extends VariantProps<typeof accessStateVariants> {
  /** Primary call-to-action, e.g. `<Button asChild><Link/></Button>`. */
  action?: React.ReactNode;
  className?: string;
  /** String, or rich nodes (multiple paragraphs, a mailto link, etc.). */
  description?: React.ReactNode;
  /** Optional Lucide icon rendered in the tinted badge. Omit for no badge. */
  icon?: React.ReactNode;
  iconVariant?: IconVariant;
  /** Secondary action rendered beneath / beside the primary one. */
  secondaryAction?: React.ReactNode;
  title: string;
}

export function AccessState({
  icon,
  iconVariant = "default",
  title,
  description,
  action,
  secondaryAction,
  size = "page",
  align = "center",
  className,
}: AccessStateProps) {
  const hasActions = Boolean(action || secondaryAction);

  return (
    <div
      className={cn(accessStateVariants({ size, align }), className)}
      data-slot="access-state"
    >
      {icon && (
        <div
          aria-hidden="true"
          className={cn(
            "flex size-12 shrink-0 items-center justify-center rounded-2xl [&_svg:not([class*='size-'])]:size-6",
            ICON_VARIANT_CLASSES[iconVariant]
          )}
          data-slot="access-state-icon"
        >
          {icon}
        </div>
      )}

      <div className="space-y-2">
        <h2
          className={cn(titleVariants({ size }))}
          data-slot="access-state-title"
        >
          {title}
        </h2>
        {description && (
          <div
            className={cn(
              "max-w-sm space-y-1.5 text-muted-foreground text-sm",
              align === "center" && "mx-auto"
            )}
            data-slot="access-state-description"
          >
            {description}
          </div>
        )}
      </div>

      {hasActions && (
        <div
          className={cn(
            "flex flex-col gap-2 sm:flex-row",
            align === "center" && "justify-center"
          )}
          data-slot="access-state-actions"
        >
          {action}
          {secondaryAction}
        </div>
      )}
    </div>
  );
}
